import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, standardNodeCreator } from './utils'

/**
 * Subtracts all provided numbers (subtrahends) from the first number (minuend)
 *
 * @param expressions - List of expressions to subtract from the first number
 * @example
 * { '-': [ 6, 2, 1 ] }
 * // ->
 * 3
 */
export interface Subtract<out T = any> {
  /** {@link Subtract} */
  readonly [Key.Subtract]: T extends number
    ? readonly Expression<number>[]
    : never
}
export namespace Subtract {
  export const isSubtract: TypeChecker<Subtract> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.object<Subtract>({
        [Key.Subtract]: CommonArgs.isListOfExpressions as TypeChecker<
          Subtract[Key.Subtract]
        >,
      }),
  ).usingAlias('Fn.Subtract')
  export const assertIsSubtract: TypeAsserter<Subtract> =
    TypeAsserter.Factory.from(isSubtract)

  /**
   * @see {@link Node}
   */
  export class Node<T extends number = number> extends fnNode.Node<
    T,
    Key.Subtract,
    {
      readonly minuend: expressionNode.Node
      readonly subtrahends: readonly expressionNode.Node[]
    }
  > {
    // NOTE: done as getter in case underlying nodes have getters
    public get type(): common.value.types.Type {
      return (
        [this.arg.minuend, ...this.arg.subtrahends].find(node =>
          common.value.types.Intrinsic.ForNumber.isForNumber(node.type),
        )?.type ?? common.value.types.Intrinsic.Decimal
      )
    }

    constructor([minuend, ...subtrahends]: readonly expressionNode.Node[]) {
      super(Key.Subtract, { minuend, subtrahends })
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<Subtract>['expressionsInArgs'] = fn =>
    fn[Key.Subtract]
  export const HELPER: FnHelper<Subtract> = {
    createNode: standardNodeCreator.usingExpressionList({
      // If there is nothing to subtract, the result is 0
      emptyValue: 0,
      finalizeArgs: (args, stash) => [
        // If we have a minuend, that is the first argument
        ...(stash.minuend ? [stash.minuend] : []),
        // Then we put all subtrahend expressions
        ...args,
        // Finally, if the computed subtrahend is non-zero, we put that at the
        // end (this will never happend if the minuend is a value node)
        ...(stash.knownSubtrahend === 0
          ? []
          : [new expressionNode.ForValue(stash.knownSubtrahend)]),
      ],
      getArgExpressions: (fn, stash, context) => {
        const args = expressionsInArgs(fn)
        if (args.length === 0) {
          return args
        }
        // We split out the minuend argument and process that separately
        const minuendArg = args[0]
        const subtrahendArgs = args.slice(1)
        stash.minuend = expressionNode.from(minuendArg, context)
        return subtrahendArgs
      },
      handleValue: (node, stash) => {
        const value = Number(node.value)
        if (expressionNode.ForValue.isForValue(stash.minuend)) {
          // If minuend is a value node, we can directly subtract from it
          stash.minuend = new expressionNode.ForValue(
            Number(stash.minuend.value) - value,
          )
        } else {
          // Otherwise, we need to keep track of the total value of all
          // pre-computed subtrahends to create one aggregate subtrahend
          stash.knownSubtrahend += Number(node.value)
        }
      },
      nodeClass: Node,
      setupStash: (): {
        minuend?: expressionNode.Node
        knownSubtrahend: number
      } => ({ knownSubtrahend: 0 }),
    }),
    expressionsInArgs,
    typeChecker: isSubtract,
  }
}
